/**
 * DashboardShareDialog Component (Dashboard Portal feature)
 *
 * Centered MODAL DIALOG for sharing a saved dashboard via a public share link.
 *
 * CLONED from apps/web/src/components/clients/CreateShareLinkSheet.tsx and adapted
 * for the dashboard model:
 *   - NO scope / reportId fields. v1 has a SINGLE share scope "dashboard"; the
 *     create body carries `dashboardId` directly.
 *   - Form: label, expiration preset, optional password, and the REUSED
 *     PortalAccessControlsCard (hidden metrics / integrations).
 *   - On create it shows the raw token + copyable share URL via the REUSED
 *     TokenRevealDialog (which reads only `link.url`).
 *   - Lists the existing share links for THIS dashboard with a revoke action.
 *
 * REUSE (import, do not clone): PortalAccessControlsCard, SelectPicker,
 * TokenRevealDialog from components/clients/**. The dashboard create/list/revoke
 * calls go through lib/dashboardShareApi (the cloned agency-side client).
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Trash, LockSimple, Link as LinkIcon } from "@phosphor-icons/react";
import {
  createDashboardShareLink,
  listDashboardShareLinks,
  revokeDashboardShareLink,
  formatDashboardShareExpiration,
  type DashboardShareExpirationPreset,
  type DashboardShareLinkWithToken,
  type DashboardShareLinkProjection,
} from "../../lib/dashboardShareApi";
import type { ToastType } from "../../hooks/useToast";
import { PortalAccessControlsCard } from "../clients/PortalAccessControlsCard";
import { SelectPicker } from "../clients/SelectPicker";
import { TokenRevealDialog } from "../clients/TokenRevealDialog";
import type { PortalLinkWithToken } from "../../lib/portalLinksApi";

interface DashboardShareDialogProps {
  /** The saved dashboard being shared. */
  dashboardId: string;
  /** The dashboard's owning client — the share link is client + dashboard scoped. */
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showToast: (type: ToastType, message: string) => void;
}

const EXPIRATION_OPTIONS: Array<{
  value: DashboardShareExpirationPreset;
  label: string;
}> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "never", label: "Never expires" },
];

/**
 * Adapt the dashboard-share link into the `PortalLinkWithToken` shape the REUSED
 * TokenRevealDialog expects. The dialog reads ONLY `link.url`; the other fields
 * are filled with safe defaults so the (frozen) prop type is satisfied without
 * editing the component.
 */
function toRevealLink(link: DashboardShareLinkWithToken): PortalLinkWithToken {
  return {
    ...link,
    reportId: null,
    costEfficiencyHidden: false,
  } as PortalLinkWithToken;
}

export function DashboardShareDialog({
  dashboardId,
  clientId,
  open,
  onOpenChange,
  showToast,
}: DashboardShareDialogProps) {
  const queryClient = useQueryClient();

  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] =
    useState<DashboardShareExpirationPreset>("30d");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [hiddenMetrics, setHiddenMetrics] = useState<string[]>([]);
  const [hiddenIntegrations, setHiddenIntegrations] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [revealLink, setRevealLink] = useState<DashboardShareLinkWithToken | null>(
    null,
  );

  // Reset on open (NOT on close) — resetting on close would flash the form to
  // empty values mid-exit-animation.
  useEffect(() => {
    if (open) {
      setLabel("");
      setExpiresAt("30d");
      setUsePassword(false);
      setPassword("");
      setHiddenMetrics([]);
      setHiddenIntegrations([]);
      setErrors({});
    }
  }, [open]);

  // Existing share links for this dashboard. The list endpoint is per-CLIENT, so
  // we filter to THIS dashboard client-side (a dashboard's links are a subset).
  const linksQuery = useQuery({
    queryKey: ["dashboard-share-links", clientId],
    enabled: open && Boolean(clientId),
    queryFn: () => listDashboardShareLinks(clientId),
  });

  const dashboardLinks = (linksQuery.data ?? []).filter(
    (l) => l.dashboardId === dashboardId,
  );

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (label.trim().length === 0) next.label = "Label is required";
    if (label.length > 120) next.label = "Label must be 120 characters or less";
    if (usePassword && password.length < 8) {
      next.password = "Password must be at least 8 characters";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const create = useMutation({
    mutationFn: () =>
      createDashboardShareLink({
        clientId,
        dashboardId,
        label: label.trim(),
        expiresAt,
        ...(usePassword && password ? { password } : {}),
        // Only include arrays when non-empty so the wire shape stays minimal.
        ...(hiddenMetrics.length > 0 ? { hiddenMetrics } : {}),
        ...(hiddenIntegrations.length > 0 ? { hiddenIntegrations } : {}),
      }),
    onSuccess: (link) => {
      setRevealLink(link);
      // Reset the form for the next link + refresh the existing-links list.
      setLabel("");
      setUsePassword(false);
      setPassword("");
      setHiddenMetrics([]);
      setHiddenIntegrations([]);
      void queryClient.invalidateQueries({
        queryKey: ["dashboard-share-links", clientId],
      });
    },
    onError: (
      err: Error & {
        apiError?: { errors?: Array<{ field: string; message: string }> };
      },
    ) => {
      const fieldErrors = err.apiError?.errors;
      if (Array.isArray(fieldErrors)) {
        const map: Record<string, string> = {};
        for (const e of fieldErrors) map[e.field] = e.message;
        setErrors(map);
      } else {
        showToast("error", err.message ?? "Failed to create share link");
      }
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeDashboardShareLink(id),
    onSuccess: () => {
      showToast("success", "Share link revoked");
      void queryClient.invalidateQueries({
        queryKey: ["dashboard-share-links", clientId],
      });
    },
    onError: (err: Error) =>
      showToast("error", err.message ?? "Failed to revoke link"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    create.mutate();
  }

  // Block close while submitting (avoids losing in-flight state).
  function handleOpenChange(next: boolean): void {
    if (!next && create.isPending) return;
    onOpenChange(next);
  }

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Share dashboard
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Create a public link to share this live dashboard. You'll
                      see the URL once after generation.
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                    >
                      <label htmlFor="link-label" className="text-sm font-medium">
                        Label <span className="text-destructive">*</span>
                        {errors.label && (
                          <span
                            className="text-sm text-destructive font-normal ml-1"
                            role="alert"
                          >
                            {errors.label}
                          </span>
                        )}
                      </label>
                      <Input
                        id="link-label"
                        value={label}
                        onChange={(e) => {
                          setLabel(e.target.value);
                          if (errors.label)
                            setErrors((prev) => ({ ...prev, label: "" }));
                        }}
                        placeholder="e.g. Q1 dashboard for CEO"
                        className={`rounded-lg ${errors.label ? "border-destructive" : ""}`}
                        maxLength={120}
                        data-testid="label-input"
                        autoFocus
                      />
                    </motion.div>

                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <label
                        htmlFor="expires-select"
                        className="text-sm font-medium"
                      >
                        Expiration
                      </label>
                      <SelectPicker
                        id="expires-select"
                        value={expiresAt}
                        onChange={(v) =>
                          setExpiresAt(v as DashboardShareExpirationPreset)
                        }
                        options={EXPIRATION_OPTIONS}
                        testId="expires-select"
                      />
                    </motion.div>

                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="use-password"
                          checked={usePassword}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUsePassword(checked);
                            if (!checked) setPassword("");
                          }}
                          className="rounded"
                          data-testid="use-password"
                        />
                        <label
                          htmlFor="use-password"
                          className="text-sm cursor-pointer"
                        >
                          Require a password
                        </label>
                      </div>
                      {usePassword && (
                        <Input
                          id="link-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          className={`rounded-lg ${errors.password ? "border-destructive" : ""}`}
                          minLength={8}
                          data-testid="password-input"
                        />
                      )}
                      {errors.password && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.password}
                        </p>
                      )}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <PortalAccessControlsCard
                        clientId={clientId}
                        hiddenMetrics={hiddenMetrics}
                        hiddenIntegrations={hiddenIntegrations}
                        onChange={(next) => {
                          setHiddenMetrics(next.hiddenMetrics);
                          setHiddenIntegrations(next.hiddenIntegrations);
                        }}
                        disabled={create.isPending}
                      />
                    </motion.div>

                    {/* Existing share links for this dashboard */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      data-testid="existing-links-section"
                    >
                      <div className="text-sm font-medium">Existing links</div>
                      {linksQuery.isLoading ? (
                        <p className="text-[13px] text-muted-foreground">
                          Loading links…
                        </p>
                      ) : dashboardLinks.length === 0 ? (
                        <p className="text-[13px] text-muted-foreground italic">
                          No share links yet for this dashboard.
                        </p>
                      ) : (
                        <ul className="space-y-2" data-testid="existing-links-list">
                          {dashboardLinks.map((link) => (
                            <ExistingLinkRow
                              key={link.id}
                              link={link}
                              onRevoke={() => revoke.mutate(link.id)}
                              revoking={
                                revoke.isPending && revoke.variables === link.id
                              }
                            />
                          ))}
                        </ul>
                      )}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button
                          type="button"
                          variant="ghost"
                          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                          style={{ border: "0.5px solid hsl(var(--input))" }}
                          onClick={() => onOpenChange(false)}
                          disabled={create.isPending}
                        >
                          Close
                        </Button>
                        <Button
                          type="submit"
                          variant="brand"
                          className="rounded-xl cursor-pointer"
                          disabled={create.isPending}
                          data-testid="submit-create-link"
                        >
                          {create.isPending ? "Generating…" : "Generate link"}
                        </Button>
                      </div>
                    </motion.div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* One-time token reveal — reused from the portal-link surface. */}
      {revealLink && (
        <TokenRevealDialog
          open={revealLink !== null}
          title="Dashboard share link created"
          link={toRevealLink(revealLink)}
          onClose={() => setRevealLink(null)}
          showToast={showToast}
        />
      )}
    </>
  );
}

/**
 * One existing share-link row: label, status/expiry summary, and a revoke button.
 */
function ExistingLinkRow({
  link,
  onRevoke,
  revoking,
}: {
  link: DashboardShareLinkProjection;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isRevoked = link.status === "revoked";
  const isExpired = link.status === "expired";
  const statusLabel = isRevoked
    ? "Revoked"
    : isExpired
      ? "Expired"
      : formatDashboardShareExpiration(link.expiresAt);

  return (
    <li
      className="flex items-center gap-3 rounded-xl border-[0.5px] border-input bg-background px-3 py-2 shadow-xs"
      data-testid="existing-link-row"
    >
      <LinkIcon size={16} className="shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-medium text-foreground">
            {link.label}
          </span>
          {link.passwordProtected && (
            <LockSimple
              size={13}
              weight="fill"
              className="shrink-0 text-muted-foreground"
              aria-label="Password protected"
            />
          )}
        </div>
        <span
          className={`text-[12px] ${
            isRevoked || isExpired ? "text-muted-foreground" : "text-foreground/60"
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {!isRevoked && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer disabled:opacity-50"
          title="Revoke link"
          aria-label={`Revoke ${link.label}`}
          data-testid="revoke-link-button"
        >
          <Trash size={15} />
        </button>
      )}
    </li>
  );
}
